import type { ServerResponse } from 'http';

import type { UserClaims } from '@okta/okta-auth-js';
import { createAction, createAsyncAction } from 'typesafe-actions';

import type {
  RichAccessToken,
  UserDeactivation,
  UserDeactivationReason,
} from '@evenfinancial/auth-client';

import type {
  ProfileMeta,
  UserGrantsData,
  UserWithRoles,
} from '@portal/api-models';
import type { EngineUserClaims, PortalError } from '@portal/common';

import type { IsomorphicRequestPayload } from '../types';

import { UserActionType } from './actionTypes';
import type {
  UserCreatePayload,
  UserDemandContactUsRequestPayload,
  UserUpdateInfo,
} from './types';

export interface DeleteAccessTokenByUuidRequest {
  subAccountUuid: string;
  uuid: string;
}

export type RichAccessTokenByUuidRequest = IsomorphicRequestPayload & {
  uuids: string[];
};

export type RichAccessTokenByIdRequest = IsomorphicRequestPayload & {
  id: number;
};

export interface RichAccessTokenByIdSuccess {
  id: number;
  richAccessToken: RichAccessToken;
}

export interface UpdateProfileMeta {
  featureFlags: string[];
  roles: string[];
  scopes: string[];
  whitelist: string[];
}

export const userRequestAction = createAsyncAction(
  UserActionType.GET_ALL_REQUEST,
  UserActionType.GET_ALL_SUCCESS,
  UserActionType.GET_ALL_FAILURE
)<IsomorphicRequestPayload, UserWithRoles[], Error>();

export const userAccessRequestAction = createAsyncAction(
  UserActionType.GET_ACCESS_ALL_REQUEST,
  UserActionType.GET_ACCESS_ALL_SUCCESS,
  UserActionType.GET_ACCESS_ALL_FAILURE
)<IsomorphicRequestPayload, UserGrantsData[], Error>();

export const userByUuidRequestAction = createAsyncAction(
  UserActionType.GET_BY_UUID_REQUEST,
  UserActionType.GET_BY_UUID_SUCCESS,
  UserActionType.GET_BY_UUID_FAILURE
)<
  IsomorphicRequestPayload & { res?: ServerResponse; uuid: string },
  UserWithRoles,
  Error
>();

export const userPwResetAction = createAsyncAction(
  UserActionType.PW_RESET_REQUEST,
  UserActionType.PW_RESET_SUCCESS,
  UserActionType.PW_RESET_FAILURE
)<string, void, Error>();

export const userVerificationAction = createAsyncAction(
  UserActionType.GET_VERIFICATION_REQUEST,
  UserActionType.GET_VERIFICATION_SUCCESS,
  UserActionType.GET_VERIFICATION_FAILURE
)<string, void, PortalError>();

export const userAccessByUuidRequestAction = createAsyncAction(
  UserActionType.GET_ACCESS_BY_UUID_REQUEST,
  UserActionType.GET_ACCESS_BY_UUID_SUCCESS,
  UserActionType.GET_ACCESS_BY_UUID_FAILURE
)<IsomorphicRequestPayload, UserGrantsData, Error>();

export const updateUserRequestAction = createAsyncAction(
  UserActionType.UPDATE_REQUEST,
  UserActionType.UPDATE_SUCCESS,
  UserActionType.UPDATE_FAILURE
)<UserUpdateInfo, UserWithRoles, Error>();

export const setClaims = createAction(UserActionType.SET_CLAIMS)<
  UserClaims<EngineUserClaims>
>();

export const setProfileMeta = createAction(
  UserActionType.SET_PROFILE_META
)<ProfileMeta>();

export const userCreateRequestAction = createAsyncAction(
  UserActionType.CREATE_REQUEST_CLIENT,
  UserActionType.CREATE_SUCCESS_CLIENT,
  UserActionType.CREATE_FAILURE_CLIENT
)<UserCreatePayload, UserWithRoles, PortalError>();

export const richAccessTokenByUuidRequestAction = createAsyncAction(
  UserActionType.GET_RICH_TOKEN_BY_UUID_REQUEST,
  UserActionType.GET_RICH_TOKEN_BY_UUID_SUCCESS,
  UserActionType.GET_RICH_TOKEN_BY_UUID_FAILURE
)<RichAccessTokenByUuidRequest, Record<string, RichAccessToken>, Error>();

export const richAccessTokenByIdRequestAction = createAsyncAction(
  UserActionType.GET_RICH_TOKEN_BY_ID_REQUEST,
  UserActionType.GET_RICH_TOKEN_BY_ID_SUCCESS,
  UserActionType.GET_RICH_TOKEN_BY_ID_FAILURE
)<RichAccessTokenByIdRequest, RichAccessTokenByIdSuccess, Error>();

export const deleteAccessTokenByUuid = createAsyncAction(
  UserActionType.DELETE_TOKEN_BY_UUID_REQUEST,
  UserActionType.DELETE_TOKEN_BY_UUID_SUCCESS,
  UserActionType.DELETE_TOKEN_BY_UUID_FAILURE
)<
  DeleteAccessTokenByUuidRequest,
  {
    subAccountUuid: string;
    uuid: string;
  },
  Error
>();

export const userDeactivationRequestAction = createAsyncAction(
  UserActionType.GET_ALL_USER_DEACTIVATION_REQUEST,
  UserActionType.GET_ALL_USER_DEACTIVATION_SUCCESS,
  UserActionType.GET_ALL_USER_DEACTIVATION_FAILURE
)<IsomorphicRequestPayload, UserDeactivation[], Error>();

export const createUserDeactivationsAction = createAsyncAction(
  UserActionType.CREATE_USER_DEACTIVATIONS_REQUEST,
  UserActionType.CREATE_USER_DEACTIVATIONS_SUCCESS,
  UserActionType.CREATE_USER_DEACTIVATIONS_FAILURE
)<
  { deactivationReason?: UserDeactivationReason; userIds: number[] },
  UserDeactivation[],
  Error
>();

export const deleteUserDeactivationAction = createAsyncAction(
  UserActionType.DELETE_USER_DEACTIVATIONS_REQUEST,
  UserActionType.DELETE_USER_DEACTIVATIONS_SUCCESS,
  UserActionType.DELETE_USER_DEACTIVATIONS_FAILURE
)<{ deactivationIds: number[] }, UserDeactivation[], Error>();

export const sendUserDemandContactUsRequestEmailAction = createAsyncAction(
  UserActionType.SEND_DEMAND_USER_CONTACT_US_REQUEST_EMAIL_REQUEST,
  UserActionType.SEND_DEMAND_USER_CONTACT_US_REQUEST_EMAIL_SUCCESS,
  UserActionType.SEND_DEMAND_USER_CONTACT_US_REQUEST_EMAIL_FAILURE
)<UserDemandContactUsRequestPayload, string, void>();

export const sendUserCreationLimitChangeRequestEmailAction = createAsyncAction(
  UserActionType.SEND_USER_CREATION_LIMIT_CHANGE_REQUEST_EMAIL_REQUEST,
  UserActionType.SEND_USER_CREATION_LIMIT_CHANGE_REQUEST_EMAIL_SUCCESS,
  UserActionType.SEND_USER_CREATION_LIMIT_CHANGE_REQUEST_EMAIL_FAILURE
)<IsomorphicRequestPayload, undefined, void>();

export const toggleCreateUser = createAction(
  UserActionType.TOGGLE_CREATE
)<undefined>();
export const toggleDuplicateUser = createAction(
  UserActionType.TOGGLE_DUPLICATE
)<string>();
export const toggleDuplicateOpen = createAction(
  UserActionType.TOGGLE_DUPLICATE_OPEN
)<string>();
